import { Alert, Button, Stack, Typography } from "@mui/material";
import ConfirmDialog from "components/common/ConfirmDialog";
import CustomButton from "components/common/CustomButton";
import { useGetConfigProfileQuery } from "features/integrationsBeta/configProfiles";
import {
  IntegrationDetail,
  useGetIntegrationConfigSchemaQuery,
  useUpdateIntegrationMutation,
} from "features/integrationsBeta/integrationsBeta";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatApiError } from "utils/formatApiError";

import {
  ConvertToProfileDialog,
  LinkProfileDialog,
} from "./ConfigProfileDialogs";
import JsonCodeField from "./JsonCodeField";
import JsonSchemaForm from "./JsonSchemaForm";
import {
  usePendingChanges,
  type PendingChange,
} from "./pending/PendingChangesContext";
import SectionCard from "./SectionCard";

type Json = Record<string, unknown>;

interface Props {
  integration: IntegrationDetail;
}

/**
 * Edits the integration's effective configuration from its registered
 * Pydantic schema (department->store / job-code maps render as editable
 * tables, flags, etc.); integrations with no registered schema fall back to
 * a raw-JSON editor.
 *
 * Edits are STAGED into the Settings pending changes (saved together from
 * the save bar). When the integration is linked to a shared config profile,
 * staging targets the PROFILE (applies to every linked integration);
 * link / unlink / convert stay immediate and discard staged config edits
 * because they change what the config edit targets.
 */
export default function IntegrationConfigForm({ integration }: Props) {
  const dispatch = useAppDispatch();
  const { canEdit } = usePlatformRole();
  const { stage, getPending, removeKeys } = usePendingChanges();

  const profileId = integration.config_profile ?? null;
  const linked = profileId != null;

  const { data, isLoading } = useGetIntegrationConfigSchemaQuery(
    integration.id,
  );
  const { data: profile } = useGetConfigProfileQuery(profileId ?? 0, {
    skip: !linked,
  });
  const [updateIntegration, { isLoading: unlinking }] =
    useUpdateIntegrationMutation();

  const [linkOpen, setLinkOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);

  const profileDetail =
    integration.config_profile_detail ??
    (profile
      ? {
          id: profile.id,
          name: profile.name,
          config_schema_key: profile.config_schema_key,
          used_by: profile.used_by,
        }
      : null);

  // Where a config edit lands: the linked profile, else the integration.
  const cfgTarget =
    linked && profileId != null
      ? {
          entityType: "configProfile" as const,
          entityId: profileId,
          entityLabel: `Shared config "${profileDetail?.name ?? `#${profileId}`}"`,
        }
      : {
          entityType: "integration" as const,
          entityId: integration.id,
          entityLabel: "Integration",
        };
  const pendingCfg = getPending(
    cfgTarget.entityType,
    cfgTarget.entityId,
    "config",
  );

  // The edited value is derived, never mirrored into local state: a staged
  // edit wins over a background refetch (a mid-edit invalidation cannot
  // clobber the draft) and disappears together with its pending record, so
  // Discard all reverts the form without a reload.
  const sourceConfig = linked ? profile?.config : integration.config;
  const value = useMemo<Json>(
    () =>
      ((pendingCfg?.newValue as Json | undefined) ??
        sourceConfig ??
        {}) as Json,
    [pendingCfg, sourceConfig],
  );

  // The raw editor owns its text while the user types; it re-syncs whenever
  // the effective config changes (refetch, apply, discard).
  const [raw, setRaw] = useState(() => JSON.stringify(value, null, 2));
  const [rawError, setRawError] = useState<string | null>(null);
  useEffect(() => {
    setRaw(JSON.stringify(value, null, 2));
    setRawError(null);
  }, [value]);

  // Link / unlink / convert (and external re-links) change the edit target;
  // staged config edits against the old target no longer make sense.
  const prevProfileRef = useRef(profileId);
  useEffect(() => {
    const prev = prevProfileRef.current;
    if (prev === profileId) return;
    prevProfileRef.current = profileId;
    const stale = [
      getPending("integration", integration.id, "config"),
      prev != null ? getPending("configProfile", prev, "config") : undefined,
      profileId != null
        ? getPending("configProfile", profileId, "config")
        : undefined,
    ].filter((c): c is PendingChange => !!c);
    if (stale.length) {
      removeKeys(stale.map((c) => c.key));
      dispatch(
        pushNotification({
          severity: "info",
          message:
            "Pending configuration changes were discarded because the config source changed.",
        }),
      );
    }
  }, [profileId]);

  const schema =
    (data?.config_schema as Record<string, unknown> | null) ?? null;

  const stageConfig = (config: Json) =>
    stage({
      ...cfgTarget,
      section: "integration_config",
      field: "config",
      fieldLabel: "Configuration",
      valueKind: "config",
      oldValue: (sourceConfig ?? {}) as Json,
      newValue: config,
      schema,
    });

  const applyRaw = () => {
    let config: Json;
    try {
      config = raw.trim() ? JSON.parse(raw) : {};
    } catch {
      setRawError("Invalid JSON.");
      return;
    }
    stageConfig(config);
  };

  const confirmUnlink = async () => {
    try {
      await updateIntegration({
        id: integration.id,
        config_profile: null,
      }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message:
            "Profile unlinked - its configuration was copied to this integration.",
        }),
      );
    } catch (err) {
      dispatch(
        pushNotification({
          severity: "error",
          message: formatApiError(
            (err as { data?: unknown })?.data,
            "Could not unlink the profile.",
          ),
        }),
      );
    } finally {
      setUnlinkOpen(false);
    }
  };

  // No registered schema AND nothing stored: the integration simply does not
  // use integration-level config - say so instead of offering a JSON editor.
  const configUnused =
    !schema &&
    Object.keys((sourceConfig as Json | undefined) ?? {}).length === 0;

  const editorBody = schema ? (
    <Stack spacing={2}>
      <JsonSchemaForm
        schema={schema}
        value={value}
        onChange={stageConfig}
        disabled={!canEdit}
      />
      {canEdit && (
        <Typography variant="caption" color="text.secondary">
          Edits are added to the pending changes and saved together from the
          save bar below.
        </Typography>
      )}
    </Stack>
  ) : configUnused ? (
    <Typography variant="body2" color="text.secondary">
      This integration does not use integration-level configuration - its
      pipeline steps carry their own settings.
    </Typography>
  ) : (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        No registered schema for this integration - editing raw JSON.
      </Typography>
      <JsonCodeField
        value={raw}
        error={rawError}
        rows={10}
        onChange={(v) => {
          setRaw(v);
          setRawError(null);
        }}
      />
      {canEdit && (
        <CustomButton fullWidth={false} onClick={applyRaw}>
          Apply
        </CustomButton>
      )}
    </Stack>
  );

  return (
    <SectionCard
      title="Integration Configuration"
      action={
        !linked && canEdit && !configUnused ? (
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setLinkOpen(true)}>
              Link profile
            </Button>
            <Button size="small" onClick={() => setConvertOpen(true)}>
              Convert to shared
            </Button>
          </Stack>
        ) : undefined
      }
    >
      {isLoading || (linked && !profile) ? (
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      ) : (
        <Stack spacing={2}>
          {linked && (
            <Alert
              severity="info"
              action={
                canEdit ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setUnlinkOpen(true)}
                  >
                    Unlink
                  </Button>
                ) : undefined
              }
            >
              Shared config:{" "}
              <strong>{profileDetail?.name ?? `#${profileId}`}</strong> - used
              by {profileDetail?.used_by ?? "?"} integration(s). Changes here
              apply to all of them.
            </Alert>
          )}
          {pendingCfg && (
            <Alert severity="warning">
              This configuration has unsaved changes - save them from the save
              bar below.
            </Alert>
          )}
          {editorBody}
        </Stack>
      )}

      <ConfirmDialog
        open={unlinkOpen}
        title="Unlink shared config?"
        message={
          "The profile's configuration is copied into this integration, so " +
          "runs keep behaving the same. Later changes to the profile no " +
          "longer apply here. Staged configuration edits are discarded."
        }
        confirmLabel="Unlink"
        onConfirm={confirmUnlink}
        onCancel={() => setUnlinkOpen(false)}
        loading={unlinking}
      />

      {linkOpen && (
        <LinkProfileDialog
          open
          onClose={() => setLinkOpen(false)}
          integration={integration}
          schemaKey={data?.config_schema_key ?? ""}
        />
      )}
      {convertOpen && (
        <ConvertToProfileDialog
          open
          onClose={() => setConvertOpen(false)}
          integration={integration}
        />
      )}
    </SectionCard>
  );
}
